import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map } from 'rxjs';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import { apiEndpoints } from 'src/app/shared/enums/api-endpoints';
import { AppSettingsResponseModel } from '../models/base';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';

registerLocaleData(localeEn);

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private currentLocaleSubject = new BehaviorSubject<string>('de-DE');
  currentLocale$ = this.currentLocaleSubject.asObservable();

  private accountSettingsSubject = new BehaviorSubject<any>('');
  public accountSettings$ = this.accountSettingsSubject.asObservable();

  default_currency_symbol: any;
  public date_format_id = '';
  public date_format_name = '';
  public date_format_code = '';
  public date_format_input_code = '';
  public date_format_code_with_time = '';
  public time_zone_id = '';
  public time_zone_name = '';
  public time_zone_offset = '';
  public time_zone_code = '';
  public currency_id = '';
  public currency_name = '';
  public currency_code = '';
  public currency_symbol = '';
  public iso_code = 'INR';
  public iso_locale = 'en-IN';
  public decimal_id = '';
  public decimal_code: any;
  public maxFractionDigits: any;
  public gridpage_size = 10;
  public is_quotes_summary: any;
  inputValue: string = '';
  public currentUrl = '';
  public timesheet_format: any;
  public profile_image_path = '';
  public is_tax: boolean = false;
  public timesheet_approval = false
  public expense_approval = false
  public account_task_heading = '';
  public account_cost_heading = '';
  public account_task_order_id = '';
  public account_cost_order_id = '';
  public default_cost_type_id: any;
  public job_default_state_id: any;
  public job_default_template_id: any;
  public job_invoiced_state_id: any;
  public job_my_list_id: any;
  public is_weekly_entry_timesheet = '';
  public is_wip_lock_date = false;
  public wip_lock_date: string = "";
  public is_invoice_lock_date = false;
  public invoice_lock_date: string = "";
  public account_phase_heading = '';
  public account_unassigned_phase_name = '';
  public job_time_allocation_id:any;
  public time_format:any;
  public org_name:any
  public quote_accepted_cost_id: any;
  public invoice_display_option_id: any;
  public invoice_payment_terms_id: string = "";
  public invoice_payment_duration: string = "";


  private client = new BehaviorSubject<any[]>([]);
  clientData = this.client.asObservable();

  constructor(private httpClient: HttpClient,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private decimalPipe: DecimalPipe
  ) {
  }

  getAppSettings(): Observable<AppSettingsResponseModel> {
    return this.httpClient.get<AppSettingsResponseModel>(apiEndpoints.appSettings).pipe(
      map((response: AppSettingsResponseModel) =>
        response
      ),
      catchError((error: AppSettingsResponseModel) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  getHostUrl(): string {
    const currentUrl = window.location.href;

    const url = new URL(currentUrl);
    if (url.hostname.includes('localhost')) {
      const url = new URL(environment.baseURL);

      return url.origin;
    } else {
      return url.origin;
    }
  }

  setAppSettingValues(response_data: any) {
    this.date_format_id = response_data.user_settings.date_format_id;
    this.date_format_name = response_data.user_settings.date_format_name;
    this.date_format_code = response_data.user_settings.date_format_code;
    this.date_format_input_code = response_data.user_settings.date_format_input_code != '' ? response_data.user_settings.date_format_input_code : 'mm/dd/yy';
    this.date_format_code_with_time = response_data.user_settings.date_format_code + ', h:mm:ss a';
    this.time_zone_id = response_data.user_settings.time_zone_id;
    this.time_zone_name = response_data.user_settings.time_zone_name;
    this.time_zone_code = response_data.user_settings.time_zone_code;
    if (parseInt(response_data.user_settings.time_zone_offset) > 0) {
      this.time_zone_offset = 'UTC+' + response_data.user_settings.time_zone_offset;
    } else {
      this.time_zone_offset = 'UTC' + response_data.user_settings.time_zone_offset;
    }
    this.currency_id = response_data.user_settings.currency_id;
    this.currency_name = response_data.user_settings.currency_name;
    this.currency_code = response_data.user_settings.currency_code;
    this.currency_symbol = 'symbol';
    this.iso_code = response_data.user_settings.iso_code;
    this.decimal_code = response_data.user_settings.decimal_code;
    let maxFractionDigits = response_data.user_settings.decimal_code.split('-');
    this.maxFractionDigits = parseInt(maxFractionDigits[1], 10);
    this.gridpage_size = parseInt(response_data.user_settings.gridpage_size);
    this.is_quotes_summary = parseInt(response_data.user_settings.is_quotes_summary);
    this.profile_image_path = response_data.user_profile.profile_image;
    this.timesheet_format = response_data.user_settings.time_format;
    this.setLocale(this.iso_locale);
    this.is_tax = response_data.user_settings.is_tax;
    this.timesheet_approval = response_data.user_settings.is_timesheet_approval === 0 ? false : true
    this.expense_approval = response_data.user_settings.is_cost_approval === 0 ? false : true;
    this.account_task_heading = response_data.user_settings.account_task_heading;
    this.account_task_order_id = response_data.user_settings.account_task_order_id;
    this.account_cost_heading = response_data.user_settings.account_cost_heading;
    this.account_cost_order_id = response_data.user_settings.account_cost_order_id;
    this.default_cost_type_id = parseInt(response_data.user_settings.default_cost_type_id);
    this.account_phase_heading = response_data.user_settings.account_phase_heading;
    this.account_unassigned_phase_name = response_data.user_settings.account_unassigned_phase_name;
    this.job_default_state_id = parseInt(response_data.user_settings.job_default_state_id);
    this.job_default_template_id = response_data.user_settings.job_default_template_id;
    this.job_invoiced_state_id = response_data.user_settings.job_invoiced_state_id;
    this.job_my_list_id = parseInt(response_data.user_settings.job_my_list_id);
    this.is_weekly_entry_timesheet = response_data.user_settings.is_weekly_entry_timesheet;
    this.is_wip_lock_date = response_data.user_settings.is_wip_lock_date == 0 ? false : true;
    this.wip_lock_date = response_data.user_settings.wip_lock_date;
    this.is_invoice_lock_date = response_data.user_settings.is_invoice_lock_date == 0 ? false : true;
    this.invoice_lock_date = response_data.user_settings.invoice_lock_date;
    this.job_time_allocation_id = response_data.user_settings.job_time_allocation_id;
    this.time_format = parseInt(response_data.user_settings.time_format);
    this.org_name = response_data.user_profile.organization_name;
    this.quote_accepted_cost_id = parseInt(response_data.user_settings.quote_accepted_cost_id);
    this.invoice_display_option_id = response_data.user_settings.invoice_display_option_id;
    this.invoice_payment_terms_id = response_data?.user_settings?.invoice_payment_terms_id;
    this.invoice_payment_duration = response_data?.user_settings?.invoice_payment_duration;

    const accountSettings = {
      account_task_heading: this.account_task_heading,
      account_cost_heading: this.account_cost_heading,
      is_weekly_entry_timesheet: this.is_weekly_entry_timesheet,
      account_phase_heading: this.account_phase_heading,
      account_task_order_id: this.account_task_order_id,
      account_cost_order_id: this.account_cost_order_id,
      default_cost_type_id: this.default_cost_type_id,
      quote_accepted_cost_id: this.quote_accepted_cost_id
    };
    this.accountSettingsSubject.next(accountSettings);
  }

  // Dynamically load and register the locale
  async setLocale(locale: string) {
    // if (locale === 'de-DE') {
    //   const localeDe = await import('@angular/common/locales/de').then(m => m.default);
    //   registerLocaleData(localeDe);
    // } else if (locale === 'en-IN') {
    //   const localeIn = await import('@angular/common/locales/en-IN').then(m => m.default);
    //   registerLocaleData(localeIn);
    // } else {
    //   const localeEn = await import('@angular/common/locales/en').then(m => m.default);
    //   registerLocaleData(localeEn);
    // }
    if (this.iso_code === 'EUR') {
      const localeIn = await import('@angular/common/locales/de').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'de-DE';
    } else if (this.iso_code === 'JPY') {
      const localeIn = await import('@angular/common/locales/ja').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'ja';
    } else if (this.iso_code === 'CNY') {
      const localeIn = await import('@angular/common/locales/zh').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'zh';
    } else if (this.iso_code === 'QAR') {
      const localeIn = await import('@angular/common/locales/ar').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'ar';
    } else if (this.iso_code === 'INR') {
      const localeIn = await import('@angular/common/locales/en-IN').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'en-IN';
    } else if (this.iso_code === 'CAD') {
      const localeIn = await import('@angular/common/locales/en-CA').then(m => m.default);
      registerLocaleData(localeIn);
      this.iso_locale = 'en-CA';
    } else if (this.iso_code === 'AUD') {
      const localeDe = await import('@angular/common/locales/en-AU').then(m => m.default);
      registerLocaleData(localeDe);
      this.iso_locale = 'en-AU';
    } else if (this.iso_code === 'USD') {
      const localeDe = await import('@angular/common/locales/en').then(m => m.default);
      registerLocaleData(localeDe);
      this.iso_locale = 'en';
    } else {
      const localeEn = await import('@angular/common/locales/en-IN').then(m => m.default);
      registerLocaleData(localeEn);
      this.iso_locale = 'en-IN';
    }
    this.currentLocaleSubject.next(locale);
  }

  getLocale(): string {
    return this.currentLocaleSubject.value;
  }

  formatCurrency(amount: number, currencyCode: string = 'USD', display: string = 'symbol', digitsInfo: string = '1.2-2'): string | null {
    if (currencyCode = 'USD')
      currencyCode = this.currency_code;
    if (display = 'symbol')
      display = this.currency_symbol;
    if (digitsInfo = '1.2-2')
      digitsInfo = this.decimal_code;
    return this.currencyPipe.transform(amount, currencyCode, display, digitsInfo);
  }

  formatDate(date: Date, format: string = 'shortDate'): string | null {
    if (format = 'shortDate')
      format = this.date_format_code;
    return this.datePipe.transform(date, format);
  }

  formatDateWithTimezone(date: Date, format: string = 'shortDate', timezone: string = 'UTC+530'): string | null {
    if (format = 'shortDate')
      format = this.date_format_code_with_time;
    if (timezone = 'UTC+530')
      timezone = this.time_zone_offset;
    return this.datePipe.transform(date, format, timezone);
  }

  formatDateToDB(date: any) {
    const d = new Date(date)
    return this.datePipe.transform(d, 'yyyy-MM-dd')
  }

  formatedDateToDB(dateStr: string): string | null {
    const [day, month, year] = dateStr.split('/');
    const parsedDate = new Date(+year, +month - 1, +day); // Month is 0-indexed

    return this.datePipe.transform(parsedDate, 'yyyy-MM-dd');
  }

  formatDateToUI(date: any) {
    if (this.date_format_code === '') {
      return this.datePipe.transform(date, 'MM/dd/yyyy');
    } else {
      return this.datePipe.transform(date, this.date_format_code);
    }
  }

  getPage(): number {
    return 1;
  }

  getLimit(is_dropdown: boolean = false): number {
    if (is_dropdown)
      return 1000;
    else
      return this.gridpage_size;
  }

  getPageLimitItems() {
    return [10, 25, 50, 100]
  }

  getSearchLength(searchValue: string) {
    if (searchValue.length == 0 || searchValue.length >= 3)
      return true;
    else
      return false;
  }

  time_zone_convertion_with_time(date_time: any) {
    var response = date_time.split('.')[0];
    if ((this.time_zone_id !== '' && this.time_zone_offset !== '') && (this.time_zone_id !== null && this.time_zone_offset !== null)) {
      let shortDate = this.datePipe.transform(date_time.split('.')[0], 'shortDate', this.time_zone_offset);
      let shortTime = this.datePipe.transform(date_time.split('.')[0], 'shortTime', this.time_zone_offset);
      let convertedDate = this.formatDateToUI(shortDate);
      response = convertedDate + ' ' + shortTime;
    } else {
      var local = this.getTimezone();
      response = this.datePipe.transform(date_time, 'short', local);
      response = this.formatDateToUI(response);
    }
    return response;
  }

  time_zone_convertion_without_time(date_time: any) {
    var response = date_time;
    if (this.time_zone_id !== '' && this.time_zone_name !== '') {
      let shortDate = this.datePipe.transform(date_time, 'shortDate', this.time_zone_name);
      response = this.formatDateToUI(shortDate);
    } else {
      var local = this.getTimezone();
      response = this.datePipe.transform(date_time, 'shortDate', local);
    }
    return response;
  }

  getTimezone() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone;
  }

  exportToCSV(data: any[], fileName: string): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const csvOutput: string = XLSX.utils.sheet_to_csv(ws);
    const blob: Blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${fileName}.csv`);
  }

  exportToExcel(data: any[], fileName: string): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const wb: XLSX.WorkBook = { Sheets: { [fileName]: ws }, SheetNames: [fileName] };
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob: Blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, `${fileName}.xlsx`);
  }

  exportToPDF(data: any[], fileName: string): void {
    const doc = new jsPDF();
    (doc as any).autoTable({
      head: [Object.keys(data[0])],
      body: data.map(row => Object.values(row))
    });
    doc.save(`${fileName}.pdf`);
  }

  getDefaultImage(): string {
    return 'assets/images/layout/defaultimage.png';
  }

  getInvoiceLogDefatultImage() {
    return 'assets/layout/images/No-Image-Found.png';
  }

  formatNumber(value: any, maxFractionDigits: string): string {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return num.toFixed(parseInt(maxFractionDigits, 10));
    } else {
      return value; // Return the original value if it's not a number
    }
  }

 exportBlobToPdf(blob: Blob, name: any) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  exportBlobToExcel(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
  // formatNotificationDate(notificationDate: Date): any {
  //   const today = new Date();
  //   const yesterday = new Date(today);
  //   yesterday.setDate(today.getDate() - 1);

  //   const diffInTime = today.getTime() - notificationDate.getTime();
  //   const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24));

  //   if (this.isSameDay(today, notificationDate)) {
  //     return 'Today';
  //   } else if (this.isSameDay(yesterday, notificationDate)) {
  //     return 'Yesterday';
  //   } else {
  //     return this.datePipe.transform(notificationDate, 'E, MMM - d - yyyy') 
  //   }
  // }

  formatNotificationDate(notificationDate: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);
    const minutes = Math.floor(diffInSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);

    if (minutes < 1) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes} mins ago`;
    } else if (hours < 24) {
      return `${hours} hours ago`;
    } else if (days < 7) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return notificationDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  groupNotificationsByDate(notifications: any[]): any[] {
    const grouped = notifications.reduce((result, notification) => {
      const dateKey = notification.formatted_date;
      if (!result[dateKey]) {
        result[dateKey] = [];
      }
      result[dateKey].push(notification);
      return result;
    }, {});

    return Object.entries(grouped).map(([date, notifications]) => ({
      date,
      notifications
    }));
  }


  // transform(value: any): string {
  //   if (!isNaN(value)) {
  //     let currencySymbol = this.iso_code;
  //     let result = value.toString().split('.');

  //     let lastThree = result[0].substring(result[0].length - 3);
  //     let otherNumbers = result[0].substring(0, result[0].length - 3);

  //     if (otherNumbers != '') {
  //       lastThree = ',' + lastThree;
  //     }

  //     let output = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;

  //     if (result.length > 1) {
  //       output += '.' + result[1];
  //     }

  //     return currencySymbol + output;
  //   }
  //   console.log(value);
  //   return value;
  // }

  getclientData(data: any) {
    this.client.next(data);
  }
}